// ═══════════════════════════════════════════════════════════════════════════
// AI Command Schemas for the DRAW-MODIFY family (CC-2c sub-arm 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod schemas describing the JSON the AI produces when it wants to MODIFY
// existing geometry with the draw-modify editing commands. As with the block
// schemas, every schema here is a plain DATA contract — it does NOT mutate
// anything. Translation to internal `EditorCommand`s happens in
// `drawModifyDispatcher.ts`, and the reducer pipeline (the same path UI
// dispatches through) is the only place that ever writes to the document. This
// enforces the CLAUDE.md rule that AI never mutates the document directly and
// that geometry only ever changes through the Command Engine.
//
// SCOPE — the draw-modify commands that are BOTH `aiCallable: true` in the
// registry AND listed AI-callable in ТЗ §10.1, MINUS the arms owned by other
// CC-2c sub-arms:
//   • Included (this sub-arm):
//       TRIM       (FF-3 — was a false-FULL: marked done with NO AI path)
//       EXTEND, OFFSET, MIRROR, SCALE, STRETCH,
//       ARRAYRECT  (ТЗ §10.1 ARRAYRECT/ARRAY = yes)
//       ARRAYEDIT  (ТЗ §10.1 ARRAYEDIT = yes)
//   • Excluded — not aiCallable (absent from ТЗ §10.1; registry aiCallable:false):
//       FILLET, CHAMFER, BREAK, JOIN, LENGTHEN, ARRAYPOLAR, ARRAYPATH
//       (ARRAYPOLAR/ARRAYPATH are MANDATORY-false — ТЗ §10.2 defers polar/path
//        array CREATION to future scope).
//   • Excluded — owned by sibling sub-arms:
//       MOVE / COPY / ROTATE → base-modify (CC-2c sub-arm 4)
//       EXPLODE → already owned by the BLOCK domain (`action: "EXPLODE"` in
//        blockSchemas, targets registry id EXPLODE). Adding a second EXPLODE
//        member here would collide on the `action` discriminator; the general
//        `geometry/explode-entities` AI surface (non-INSERT compound entities)
//        is a documented follow-up that must EXTEND the block EXPLODE schema,
//        not introduce a colliding action here.
//
// Design notes (mirror blockSchemas.ts):
//   - Every action carries a literal `action: "..."` discriminator so the
//     top-level `drawModifyAiCommandSchema` is a Zod discriminated union, and
//     so the action sets stay DISJOINT from the block domain (enables the
//     `aiDispatcher.ts` router to pick the owning domain by `action`).
//   - Point objects reuse `aiPoint2DSchema` from blockSchemas ({ x, y }) — the
//     exact shape the target EditorCommand contracts use — so there is one
//     point-contract truth across the package (no duplicated 2D point schema).
//   - `axisAngle` is in DEGREES and passed THROUGH verbatim: the array reducer
//     converts degrees → radians itself (`arrayReducers.ts`:
//     `angleRad = axisAngle * Math.PI / 180`). The descriptor's "radians"
//     comment is stale — the reducer is the source of truth, so NO conversion
//     happens in the dispatcher (a degrees→radians conversion here would be a
//     double-conversion bug).

import { z } from "zod";

import { aiPoint2DSchema } from "./blockSchemas";

// Degeneracy floor — MUST match the downstream contract's private FLOAT_EPSILON
// (packages/contracts/src/editor.ts:4 = 1e-12). Tightening the AI schemas to
// the SAME floor means a sub-epsilon SCALE factor / OFFSET distance /
// near-coincident MIRROR axis is rejected fail-closed at the AI boundary,
// instead of passing the AI schema and then THROWING at the contract parse
// (which would open a history batch and then abort mid-dispatch).
const FLOAT_EPSILON = 1e-12;

// ═══════════════════════════════════════════════════════════════════════════
// TRIM (FF-3) — remove the portion of `entityId` bounded by the pick point and
// the nearest intersections with the cutting edges.
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps to AutoCAD TRIM / the `geometry/trim-entity` EditorCommand. FF-3: TRIM
// was a false-FULL (registry `aiCallable: true`, reducer implemented, but NO
// AI dispatcher path existed) — this schema + its dispatcher arm close that.
export const trimAiSchema = z.object({
  action: z.literal("TRIM"),
  entityId: z.string().min(1),
  cuttingEdgeIds: z.array(z.string().min(1)).min(1),
  pickPoint: aiPoint2DSchema,
});
export type TrimAiCommand = z.infer<typeof trimAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// EXTEND — lengthen `entityId` until it meets one of the boundary edges.
// ═══════════════════════════════════════════════════════════════════════════
export const extendAiSchema = z.object({
  action: z.literal("EXTEND"),
  entityId: z.string().min(1),
  boundaryEdgeIds: z.array(z.string().min(1)).min(1),
  pickPoint: aiPoint2DSchema,
});
export type ExtendAiCommand = z.infer<typeof extendAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// OFFSET — build a parallel copy of `entityId` at `distance` on the side that
// contains `sidePoint`.
// ═══════════════════════════════════════════════════════════════════════════
//
// `distance` is POSITIVE: the contract refuses non-positive offsets (negative
// not yet supported for circle/arc); the side is chosen by `sidePoint`, never
// by a sign, so there is no boolean-for-structure stand-in.
export const offsetAiSchema = z.object({
  action: z.literal("OFFSET"),
  entityId: z.string().min(1),
  // `> FLOAT_EPSILON` (not merely `.positive()`): a sub-epsilon distance like
  // 1e-20 is positive but the `geometry/offset-entity` contract rejects it, so
  // it must fail-closed HERE, before any EditorCommand / history batch exists.
  distance: z.number().refine((d) => d > FLOAT_EPSILON, {
    message:
      "Offset distance must be greater than 1e-12 (sub-epsilon / non-positive rejected)",
  }),
  sidePoint: aiPoint2DSchema,
});
export type OffsetAiCommand = z.infer<typeof offsetAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// MIRROR — mirror the selection across the (mirrorLine1 → mirrorLine2) axis.
// ═══════════════════════════════════════════════════════════════════════════
//
// `deleteSource` controls whether the originals are removed (AutoCAD's
// "Erase source objects?" prompt). The axis must be nondegenerate — the same
// refine the `geometry/mirror-entities` contract enforces, applied here so a
// malformed proposal is rejected before any EditorCommand is produced.
export const mirrorAiSchema = z
  .object({
    action: z.literal("MIRROR"),
    entityIds: z.array(z.string().min(1)).min(1),
    mirrorLine1: aiPoint2DSchema,
    mirrorLine2: aiPoint2DSchema,
    deleteSource: z.boolean().default(false),
    // MIRRTEXT — 0 = mirrored text stays readable; 1 = text mirrors fully.
    // OPTIONAL, NOT defaulted (Codex #342 F2): the AI safe contour is "AI
    // proposes, executor coerces". A hard `default(0)` froze 0 into the command
    // at parse time, so an AI MIRROR ignored a live `SETVAR MIRRTEXT 1`. Leaving
    // it `undefined` when omitted lets the translator stamp the LIVE sysvar
    // (threaded via `AiDispatchContext.mirrtext`), exactly like the draft/CLI
    // path stamps `useSysvarStore` in `useDraftSession`. An explicit value in
    // the proposal still wins; the final fallback is the AutoCAD default 0.
    mirrtext: z.number().int().min(0).max(1).optional(),
  })
  .refine(
    (cmd) => {
      const dx = cmd.mirrorLine2.x - cmd.mirrorLine1.x;
      const dy = cmd.mirrorLine2.y - cmd.mirrorLine1.y;
      // `> FLOAT_EPSILON` (not `> 0`): match the `geometry/mirror-entities`
      // contract floor so a near-coincident axis (length ≤ 1e-12) is rejected
      // here rather than throwing at the contract mid-dispatch.
      return Math.hypot(dx, dy) > FLOAT_EPSILON;
    },
    { message: "Mirror axis must be nondegenerate (the two points must differ)" },
  );
export type MirrorAiCommand = z.infer<typeof mirrorAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SCALE — uniformly scale the selection about `basePoint` by `factor`.
// ═══════════════════════════════════════════════════════════════════════════
//
// `factor` must be POSITIVE and above the float-epsilon floor. AutoCAD's SCALE
// is uniform — a single factor, never a per-axis pair, matching the
// `geometry/scale-entities` contract. Two boundaries are enforced here that the
// downstream contract does NOT (so they must fail-closed at the AI boundary):
//   • sub-epsilon (|f| ≤ 1e-12): the contract's `Math.abs(f) > FLOAT_EPSILON`
//     rejects it, so it would throw mid-dispatch instead of failing closed.
//   • negative: the SCALE registry descriptor forbids negative factors
//     ("Отрицательные значения не допускаются"); the contract only checks
//     `abs(f)`, so a negative factor would actually mirror-and-scale geometry —
//     outside the documented AI SCALE surface. Reject it here.
export const scaleAiSchema = z.object({
  action: z.literal("SCALE"),
  entityIds: z.array(z.string().min(1)).min(1),
  basePoint: aiPoint2DSchema,
  factor: z
    .number()
    .refine((f) => Number.isFinite(f) && f > FLOAT_EPSILON, {
      message:
        "Scale factor must be a positive finite number greater than 1e-12 (negative / sub-epsilon rejected)",
    }),
});
export type ScaleAiCommand = z.infer<typeof scaleAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// STRETCH — translate the vertices of the selection that fall INSIDE the
// crossing window by `delta`; vertices outside stay fixed.
// ═══════════════════════════════════════════════════════════════════════════
//
// `stretchWindow` is the crossing-selection rectangle ({x1,y1,x2,y2}) — a
// typed object, not a flattened number-bag — matching the contract exactly.
export const stretchAiSchema = z.object({
  action: z.literal("STRETCH"),
  entityIds: z.array(z.string().min(1)).min(1),
  stretchWindow: z.object({
    x1: z.number(),
    y1: z.number(),
    x2: z.number(),
    y2: z.number(),
  }),
  delta: aiPoint2DSchema,
});
export type StretchAiCommand = z.infer<typeof stretchAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// ARRAYRECT — rectangular associative array of the selection.
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors AutoCAD ARRAYRECT / `geometry/create-arrayrect`. `arrayGroupId` is
// "generated by the caller (dispatch layer)" per the contract; the AI normally
// omits it (a fresh group) and the dispatcher mints one (`arr-<uuid>`, matching
// the draft state machine). It stays an OPTIONAL override so a single AI turn
// can create an array and immediately ARRAYEDIT it by a known id.
// `axisAngle` is in DEGREES (see the module header — the reducer converts).
export const arrayRectAiSchema = z.object({
  action: z.literal("ARRAYRECT"),
  entityIds: z.array(z.string().min(1)).min(1),
  basePoint: aiPoint2DSchema,
  rowCount: z.number().int().positive(),
  columnCount: z.number().int().positive(),
  rowSpacing: z.number(),
  columnSpacing: z.number(),
  axisAngle: z.number().default(0),
  arrayGroupId: z.string().min(1).optional(),
});
export type ArrayRectAiCommand = z.infer<typeof arrayRectAiSchema>;

// ── ARRAYEDIT parameter sub-shapes (full-fidelity rect/polar/path) ──────────
//
// ARRAYEDIT (registry aiCallable:true) edits an EXISTING associative array of
// ANY kind. Editing a human-authored polar/path array is a legitimate modify
// even though AI CREATION of polar/path arrays is deferred (ТЗ §10.2) — so the
// full rect/polar/path payload is preserved here (no simplification). Each
// sub-shape matches the corresponding `geometry/arrayedit` contract block 1:1.
const arrayEditRectSchema = z.object({
  rowCount: z.number().int().positive(),
  columnCount: z.number().int().positive(),
  rowSpacing: z.number(),
  columnSpacing: z.number(),
  axisAngle: z.number().default(0),
});
const arrayEditPolarSchema = z.object({
  centerPoint: aiPoint2DSchema,
  itemCount: z.number().int().min(2),
  fillAngle: z.number().default(360),
  rotateItems: z.boolean().default(true),
  rowCount: z.number().int().min(1).default(1),
  rowSpacing: z.number().default(0),
});
const arrayEditPathSchema = z
  .object({
    method: z.enum(["divide", "measure"]),
    itemCount: z.number().int().min(1).optional(),
    itemSpacing: z.number().positive().optional(),
    alignItems: z.boolean().default(true),
    pathEntityId: z.string().min(1),
  })
  // method → param cross-refine. Without it `{method:"divide", itemCount:1}` or
  // `{method:"measure"}` (no itemSpacing) pass the field-level checks but the
  // path-array reducer treats them as a NO-OP — so the AI silently does nothing.
  // Reject them at the boundary: DIVIDE needs ≥2 items, MEASURE needs a spacing.
  .refine(
    (p) =>
      p.method === "divide"
        ? p.itemCount !== undefined && p.itemCount >= 2
        : p.itemSpacing !== undefined,
    {
      message:
        "ARRAYEDIT path: 'divide' requires itemCount >= 2; 'measure' requires itemSpacing > 0",
    },
  );

// ═══════════════════════════════════════════════════════════════════════════
// ARRAYEDIT — re-parameterise an existing associative array group.
// ═══════════════════════════════════════════════════════════════════════════
//
// `arrayGroupId` is REQUIRED (the AI is editing a group that already exists).
// Exactly the params object matching `mode` must be supplied — the refine
// rejects a `mode`/payload mismatch at the schema boundary (fail-closed),
// rather than deferring solely to the reducer.
export const arrayEditAiSchema = z
  .object({
    action: z.literal("ARRAYEDIT"),
    arrayGroupId: z.string().min(1),
    mode: z.enum(["rect", "polar", "path"]),
    rect: arrayEditRectSchema.optional(),
    polar: arrayEditPolarSchema.optional(),
    path: arrayEditPathSchema.optional(),
  })
  .refine(
    (cmd) =>
      (cmd.mode === "rect" && cmd.rect !== undefined) ||
      (cmd.mode === "polar" && cmd.polar !== undefined) ||
      (cmd.mode === "path" && cmd.path !== undefined),
    {
      message:
        "ARRAYEDIT requires the parameter block matching `mode` (rect/polar/path) to be present",
    },
  );
export type ArrayEditAiCommand = z.infer<typeof arrayEditAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Top-level draw-modify AI command discriminated union
// ═══════════════════════════════════════════════════════════════════════════
//
// 8 draw-modify AI schemas:
//   Edit:      TRIM (FF-3), EXTEND, OFFSET
//   Transform: MIRROR, SCALE, STRETCH
//   Array:     ARRAYRECT, ARRAYEDIT
export const drawModifyAiCommandSchema = z.discriminatedUnion("action", [
  trimAiSchema,
  extendAiSchema,
  offsetAiSchema,
  mirrorAiSchema,
  scaleAiSchema,
  stretchAiSchema,
  arrayRectAiSchema,
  arrayEditAiSchema,
]);
export type DrawModifyAiCommand = z.infer<typeof drawModifyAiCommandSchema>;

// A batch of draw-modify commands the AI can emit in one response. The
// dispatcher resolves each in order.
export const drawModifyAiCommandBatchSchema = z.array(drawModifyAiCommandSchema);
export type DrawModifyAiCommandBatch = z.infer<
  typeof drawModifyAiCommandBatchSchema
>;

// Convenience: literal action names for the router, guard map, error messages
// and telemetry. Kept in sync with the union by the exhaustive guard Record.
export const DRAW_MODIFY_AI_ACTIONS = [
  "TRIM",
  "EXTEND",
  "OFFSET",
  "MIRROR",
  "SCALE",
  "STRETCH",
  "ARRAYRECT",
  "ARRAYEDIT",
] as const;
export type DrawModifyAiAction = (typeof DRAW_MODIFY_AI_ACTIONS)[number];

const DRAW_MODIFY_ACTION_SET: ReadonlySet<string> = new Set(
  DRAW_MODIFY_AI_ACTIONS,
);

/**
 * Router predicate: does this AI command belong to the draw-modify domain?
 *
 * The draw-modify and block action literal sets are DISJOINT, so a membership
 * test on `action` is a sound discriminator. The type predicate lets
 * `aiDispatcher.ts` narrow a `BlockAiCommand | DrawModifyAiCommand` union to
 * the owning domain without importing the top-level `AiCommand` type here
 * (which would create a module cycle).
 */
export function isDrawModifyAiCommand(
  command: { action: string },
): command is DrawModifyAiCommand {
  return DRAW_MODIFY_ACTION_SET.has(command.action);
}
